import { color, icon, image, named, num, text, textarea, toggle, type Choice } from '../../themes/settingFields';
import { element } from './definition';
import { hairline, look, pad, paint, radius, round, strip } from './styleTokens';

// Pictures, glyphs and players. Every one of them is a leaf, and every one of them ships empty: a
// stock photograph standing in for a merchant's own is an invented shop fact, so the default is the
// blank frame that asks to be filled rather than a picture of somebody else's product.
//
// Three of the settings here are a value the CSS spells one way and a merchant reads another —
// `scale-down` is "Scale down", `16/9` is "16:9", `file` is "Custom". `named` is what keeps the stored
// value machine-shaped while the inspector shows the words LightFunnels shows.

/** How a picture fills its frame — `object-fit`, in the wording the Image panel uses for it. */
const IMAGE_FITS: readonly Choice[] = [
  ['cover', 'Cover'], ['contain', 'Contain'], ['fill', 'Fill'], ['none', 'None'], ['scale-down', 'Scale down']
];

/** Where a cropped picture keeps its subject, as Shopify's focal point does, in the words a merchant picks it by. */
const FOCAL_POINTS: readonly Choice[] = [
  ['center', 'Centre'], ['top', 'Top'], ['bottom', 'Bottom'], ['left', 'Left'], ['right', 'Right'],
  ['top left', 'Top left'], ['top right', 'Top right'], ['bottom left', 'Bottom left'], ['bottom right', 'Bottom right']
];

/** The three places a video plays from. `file` is what LightFunnels' *Custom video* card lands as. */
const PLATFORMS: readonly Choice[] = [['file', 'Custom'], ['youtube', 'YouTube'], ['vimeo', 'Vimeo']];

/** One picture. To make it a link, put it inside a Block link, as LightFunnels does. */
export const imageElement = element('image', {
  label: 'Image',
  description: 'One picture, with the alt text a search engine reads. Put it in a Block link to make it clickable.',
  category: 'Media',
  icon: 'image',
  props: [
    // Source, Title, Alt Text, Image fit — the four rows LightFunnels' Image panel shows, in its order,
    // and nothing after them. It used to go on: a link and where it opens, a loading mode, the file's
    // pixels — five rows a merchant never filled in, on the one panel every product page opens most. A
    // picture that is a link is a picture inside a Block link, which is how LightFunnels does it too.
    // Alt text is a box rather than a line because a sentence describing a photograph is what belongs in it.
    image('src', 'Source'),
    // The fifth row, and the one exception to "nothing after them": a phone is not a narrow desktop.
    // Cropping is the one thing `object-fit` cannot do for you — a 1.89 landscape shown in a 1.22 frame
    // keeps the frame and loses whatever was at the edges of the shot. Left empty it costs nothing:
    // the renderer draws the same single `img` it always did.
    {
      ...image('mobileSrc', 'Phone image'),
      help: 'Shown under 768px instead of the source above. Leave it empty to use one picture everywhere.'
    },
    text('title', 'Title'), textarea('alt', 'Alt Text'), named('fit', 'Image fit', IMAGE_FITS),
    { ...named('focus', 'Focal point', FOCAL_POINTS), help: 'The part of the picture kept in view when its frame crops it.' },
    { ...toggle('bundleHotspots', 'Show bundle product markers'), help: 'Inside a product bundle, marks each item on this image.' }
  ],
  defaults: {
    // `fit` is the setting and `objectFit` is not in the landing styles, so the frame has one owner and
    // a picture whose styles an older tree pinned keeps drawing exactly as that tree left it.
    props: { src: '', mobileSrc: '', title: '', alt: '', fit: 'cover' },
    styles: look({
      display: 'block', width: '100%', maxWidth: '100%', height: 'auto',
      backgroundColor: paint('surfaceAlt'), ...radius(round('md'))
    })
  }
});

/** A single glyph from the icon set, drawn at its own pixel size and in the colour its styles give it. */
export const iconElement = element('icon', {
  label: 'Icon',
  description: 'A single glyph — a tick, a lorry, a padlock — beside a promise or on its own.',
  category: 'Media',
  icon: 'shapes',
  props: [
    // Icon, then Size and Icon colour — LightFunnels' *Color & size* group, which is where a merchant
    // looks for a glyph's colour before they think to open the Styles tab. Empty means the styles' own.
    icon('name', 'Icon'), num('size', 'Size', 8, 512), color('color', 'Icon color'),
    // A tick drawn at weight 1 beside 14px copy and the same tick at weight 3 over a hero are two
    // different marks; lucide draws both, and the renderer had the weight welded to 2.
    num('strokeWidth', 'Line thickness', 1, 4), text('label', 'Accessible label')
  ],
  defaults: {
    props: { name: 'check', size: 22, color: '', strokeWidth: 2, label: '' },
    styles: look({ display: 'inline-flex', width: '22px', height: '22px', color: paint('primary') })
  }
});

/**
 * A player: a YouTube or Vimeo address, or a file the merchant hosts themselves.
 *
 * LightFunnels draws three Add-panel cards over this one thing — Custom video, Youtube video, Vimeo
 * video — and every one of them opens the same panel led by a **Platform** select. The three differ
 * only in which toggles that panel then shows: Custom offers Loop and Show Controls, YouTube offers
 * Show Controls, Vimeo offers Loop and Show Title. One type carrying all of them is the same product,
 * so the cards in `libraryPicks.ts` stay three and the definition stays one.
 */
export const videoElement = element('video', {
  label: 'Video',
  description: 'A player for a YouTube or Vimeo video, or a file you host yourself, with its poster frame.',
  category: 'Media',
  icon: 'video',
  props: [
    named('provider', 'Platform', PLATFORMS),
    // One Source row, as all three platforms show: the address of a hosted file, or the YouTube or Vimeo
    // page a merchant copied out of the address bar — a bare id pasted there works too. It is a plain
    // box, not the link picker: a video's address is never a product or a place on this page.
    text('src', 'Source'),
    toggle('autoplay', 'AutoPlay'), toggle('controls', 'Show Controls'),
    toggle('loop', 'Loop'), toggle('showTitle', 'Show Title'),
    // Ours, past LightFunnels' six: the frame a hosted file shows before it plays.
    image('poster', 'Poster frame')
  ],
  defaults: {
    // Controls stay on where LightFunnels ships them off: it draws its own play overlay over a bare frame
    // and we do not, so shipping them off would land a player nothing on the page can start. A video
    // that autoplays is muted, because that is the only autoplay a browser honours.
    props: { provider: 'youtube', src: '', autoplay: false, controls: true, loop: false, showTitle: false, poster: '' },
    // The box is a style, as LightFunnels keeps it — Styles › Sizes › Aspect ratio — and not also a
    // setting: two owners of one number is which one wins found out by accident.
    styles: look({
      width: '100%', aspectRatio: '16/9', overflow: 'hidden', backgroundColor: paint('dark'), ...radius(round('md'))
    })
  }
});

/** A hosted audio track with its artwork — a recorded testimonial, or a note from the founder. */
export const audioElement = element('audio', {
  label: 'Audio player',
  description: 'A hosted audio track the visitor can play without leaving the page.',
  category: 'Media',
  icon: 'music',
  // Source is the whole of LightFunnels' panel, and the whole of ours: a title, a speaker, artwork and a
  // loop switch used to follow it, four rows for a caption nobody had asked for on a player.
  props: [text('src', 'Source')],
  defaults: {
    props: { src: '' },
    styles: look({
      ...strip('12px'), width: '100%', color: paint('text'),
      backgroundColor: paint('surfaceAlt'), ...pad('12px', '16px'), ...hairline(), ...radius(round('md'))
    })
  }
});
